import React from "react";
import LogoOneDoor from "@shared/components/LogoOneDoor";
import { buildAdminPath, getAdminViewFromPath } from "../config/routes";

const SIDEBAR_BG = "#003D4C";
const SIDEBAR_ACTIVE = "#004D5C";
const SIDEBAR_BORDER = "#E5E7EB";
const SIDEBAR_TEXT = "#E8F4F8";
const SIDEBAR_MUTED = "#B8D4DB";

function SidebarCollapseToggle({ isCollapsed, onToggleCollapse }) {
  return (
    <button
      onClick={onToggleCollapse}
      className="absolute z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
      style={{
        ...(isCollapsed
          ? { left: "0.75rem", top: "1.5rem", backgroundColor: SIDEBAR_ACTIVE, color: "white" }
          : { right: 0, top: "1.5rem", marginRight: "-0.75rem", backgroundColor: "white", color: SIDEBAR_BG }),
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = isCollapsed ? "#006070" : "#E8F4F8";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isCollapsed ? SIDEBAR_ACTIVE : "white";
      }}
      aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
    >
      {isCollapsed ? "›" : "‹"}
    </button>
  );
}

function SidebarHeader() {
  return (
    <div className="px-6 py-4 border-b h-20 flex items-center" style={{ borderColor: SIDEBAR_BORDER }}>
      <div className="flex items-center gap-3 pr-8">
        <LogoOneDoor />
        <div>
          <h1 className="text-lg font-bold">OneDoor</h1>
          <p className="text-xs" style={{ color: SIDEBAR_MUTED }}>
            Merch Cloud Portal
          </p>
        </div>
      </div>
    </div>
  );
}

function SidebarFooter({ lastSignIn }) {
  const DEFAULT_TIMESTAMP = new Date().toISOString();
  const value = new Date(lastSignIn || DEFAULT_TIMESTAMP).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="p-4 border-t" style={{ borderColor: SIDEBAR_BORDER }}>
      <div className="text-xs" style={{ color: SIDEBAR_MUTED }}>
        Last sign in: {value}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      className="pt-4 pb-2 px-4 text-xs font-semibold uppercase tracking-wider"
      style={{ color: SIDEBAR_MUTED }}
    >
      {children}
    </div>
  );
}

function WarningBanner({ children }) {
  return (
    <div
      className="px-4 py-3 rounded-lg text-sm border"
      style={{ backgroundColor: "rgba(245, 158, 11, 0.15)", borderColor: "#D97706", color: "#B45309" }}
      role="alert"
    >
      {children}
    </div>
  );
}

function NavButton({
  active,
  onClick,
  icon,
  title,
  subtitle,
  ariaLabel,
  compact = false,
}) {
  const baseClass =
    "w-full text-left px-4 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2";

  const style = {
    backgroundColor: active ? SIDEBAR_ACTIVE : "transparent",
    color: active ? "white" : SIDEBAR_TEXT,
  };

  const applyHover = (over, el) => {
    if (active) return;
    el.style.backgroundColor = over ? SIDEBAR_ACTIVE : "transparent";
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
        style={style}
        aria-label={ariaLabel || title}
        aria-current={active ? "page" : undefined}
      >
        {icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClass}
      style={style}
      onMouseEnter={(e) => applyHover(true, e.currentTarget)}
      onMouseLeave={(e) => applyHover(false, e.currentTarget)}
      role="menuitem"
      aria-label={ariaLabel || title}
      aria-current={active ? "page" : undefined}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{icon}</span>
        <div>
          <div className="font-medium">{title}</div>
          {subtitle ? (
            <div className="text-xs" style={{ color: SIDEBAR_MUTED }}>
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function SidebarCollapsedNav({
  isDashboard,
  dashboardPath,
  onNavigate,
  hasUserManagementAccess,
  userManagementConfigured,
  items,
  getPathForView,
  adminView,
}) {
  const handleNav = (path) => {
    if (onNavigate && path) onNavigate(path);
  };

  return (
    <div className="flex flex-col items-center pt-20 gap-4">
      <NavButton
        compact
        active={isDashboard}
        onClick={() => handleNav(dashboardPath || "/")}
        icon="🏠"
        title="Dashboard"
        ariaLabel="Dashboard"
      />
      {hasUserManagementAccess &&
        userManagementConfigured &&
        items.map((item) => {
          const path = getPathForView(item.view);
          const active = adminView === item.view;
          return (
            <NavButton
              key={item.id}
              compact
              active={active}
              onClick={() => handleNav(path)}
              icon={item.icon}
              title={item.name}
              ariaLabel={item.name}
            />
          );
        })}
    </div>
  );
}

function SidebarExpandedNav({
  isDashboard,
  dashboardPath,
  onNavigate,
  hasUserManagementAccess,
  userManagementConfigured,
  items,
  getPathForView,
  adminView,
}) {
  const handleNav = (path) => {
    if (onNavigate && path) onNavigate(path);
  };

  const subtitleForAccessItem = (id) => {
    if (id === "users") return "Manage user accounts";
    if (id === "roles") return "Manage roles and assignments";
    if (id === "permission-groups") return "View and assign permission groups";
    return "";
  };

  return (
    <div className="flex-1 px-4 py-6 overflow-y-auto">
      <ul className="space-y-2" role="menu">
        <li role="none">
          <NavButton
            active={isDashboard}
            onClick={() => handleNav(dashboardPath || "/")}
            icon="🏠"
            title="My Apps Dashboard"
            subtitle="ODP Apps overview"
            ariaLabel="My Apps Dashboard"
          />
        </li>
      </ul>

      {hasUserManagementAccess ? (
        <>
          <SectionLabel>Access Management</SectionLabel>

          {userManagementConfigured ? (
            <ul className="space-y-2" role="menu">
              {items.map((item) => {
                const path = getPathForView(item.view);
                const active = adminView === item.view;

                return (
                  <li key={item.id} role="none">
                    <NavButton
                      active={active}
                      onClick={() => handleNav(path)}
                      icon={item.icon}
                      title={item.name}
                      subtitle={subtitleForAccessItem(item.id)}
                      ariaLabel={item.name}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <WarningBanner>User Management Not Configured</WarningBanner>
          )}
        </>
      ) : null}
    </div>
  );
}

export default function Sidebar({
  lastSignIn = null,
  isCollapsed = false,
  onToggleCollapse,
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
  onNavigate,
  hasUserManagementAccess = false,
  userManagementConfigured = true,
  appSlugForNav: _appSlugForNav = null,
}) {
  const adminView = getAdminViewFromPath(pathname);
  const isDashboard = !adminView;

  const dashboardPath = pathname.includes("/customer/admin/")
    ? pathname.split("/customer/admin/")[0]
    : (pathname || "").replace(/\/$/, "") || "/";

  const accessManagementItems = [
    { id: "users", name: "Users", icon: "👥", view: "users" },
    { id: "roles", name: "Roles", icon: "🎭", view: "roles" },
    { id: "permission-groups", name: "Permission Groups", icon: "🔐", view: "permission-groups" },
  ];

  const getPathForView = (view) => {
    if (view === "roles" || view === "permission-groups") {
      return buildAdminPath(pathname, view, null);
    }
    return buildAdminPath(pathname, view);
  };

  return (
    <nav
      className={`fixed left-0 top-0 h-screen flex flex-col text-white transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-72"
      }`}
      style={{ backgroundColor: SIDEBAR_BG }}
      aria-label="Customer navigation"
    >
      <SidebarCollapseToggle isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />

      {isCollapsed ? (
        <SidebarCollapsedNav
          isDashboard={isDashboard}
          dashboardPath={dashboardPath}
          onNavigate={onNavigate}
          hasUserManagementAccess={hasUserManagementAccess}
          userManagementConfigured={userManagementConfigured}
          items={accessManagementItems}
          getPathForView={getPathForView}
          adminView={adminView}
        />
      ) : (
        <>
          <SidebarHeader />
          <SidebarExpandedNav
            isDashboard={isDashboard}
            dashboardPath={dashboardPath}
            onNavigate={onNavigate}
            hasUserManagementAccess={hasUserManagementAccess}
            userManagementConfigured={userManagementConfigured}
            items={accessManagementItems}
            getPathForView={getPathForView}
            adminView={adminView}
          />
          <SidebarFooter lastSignIn={lastSignIn} />
        </>
      )}
    </nav>
  );
}
